<template>
    <div
        :id="id"
        ref="root"
        class="tl-input-toggle-container"
    >
        <div
            class="tl-input-toggle-wrapper"
            :class="{ error: error, disabled }"
            data-testid="input-toggle"
        >
            <!-- Button style toggle -->
            <button
                v-if="type === 'button'"
                :id="toggleId"
                class="tl-input-toggle-button"
                :class="{
                    'tl-input-toggle-checked': modelValue,
                    'tl-input-toggle-disabled': disabled,
                    'tl-input-toggle-error': error,
                }"
                :disabled="disabled"
                type="button"
                data-testid="tl-input-toggle-trigger"
                :aria-pressed="modelValue"
                @click="handleClick"
            >
                <span
                    v-if="effectiveLabel"
                    class="tl-input-toggle-content"
                    v-html="effectiveLabel"
                />
                <i
                    v-if="icon"
                    :class="icon"
                    class="tl-input-toggle-icon"
                />
            </button>

            <!-- Switch style toggle -->
            <label
                v-if="type === 'switch'"
                class="tl-input-toggle-switch"
            >
                <div
                    class="tl-input-toggle-slider"
                    :class="{
                        'tl-input-toggle-checked': modelValue,
                        'tl-input-toggle-disabled': disabled,
                        'tl-input-toggle-error': error,
                    }"
                >
                    <input
                        :id="toggleId"
                        type="checkbox"
                        :checked="modelValue"
                        :disabled="disabled"
                        role="switch"
                        :aria-checked="modelValue"
                        data-testid="tl-input-toggle-trigger"
                        @change="handleClick"
                    />
                    <span class="tl-input-toggle-slider-active"></span>
                </div>
                <div class="tl-input-toggle-content">{{ effectiveLabel }}</div>
            </label>

            <!-- Label style toggle -->
            <label
                v-if="type === 'label'"
                class="tl-input-toggle-label"
                :class="{
                    'tl-input-toggle-checked': modelValue,
                    'tl-input-toggle-disabled': disabled,
                    'tl-input-toggle-error': error,
                }"
            >
                <input
                    :id="toggleId"
                    type="checkbox"
                    :checked="modelValue"
                    :disabled="disabled"
                    class="visually-hidden"
                    :aria-checked="modelValue"
                    data-testid="tl-input-toggle-trigger"
                    @change="handleClick"
                />
                <span
                    v-if="effectiveLabel"
                    class="tl-input-toggle-content"
                    v-html="effectiveLabel"
                />
                <i
                    v-if="icon"
                    :class="icon"
                    class="tl-input-toggle-icon"
                />
            </label>
        </div>

        <div
            v-if="error"
            style="margin-top: 3px"
        >
            <ErrorText :error="error" />
        </div>
    </div>
</template>


<script setup lang="ts">
import { watch, computed, onMounted, ref, useId } from 'vue'
import marked from '../../utilities/marked'
import sanitize from 'widget-components/utilities/sanitize'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import type { BlockPath } from 'widget-components/integrations/CustomPage/types'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id?: string
    modelValue?: boolean
    kind?: string
    disabled?: boolean
    error?: boolean
    label?: string
    selectedLabel?: string
    selected?: boolean
    blockPath?: BlockPath
    icon?: string
    focus?: boolean
}>()

const type = computed(() => {
    return props.kind?.toLowerCase() || 'label'
})

const toggleId = computed(() => {
    return props.id ? `${props.id}-toggle` : useId()
})

const emit = defineEmits(['update:modelValue'])

const checked = defineModel<boolean>()
const root = ref<HTMLElement>()

autoFocusOnMounted(
    props,
    () => root.value?.querySelector<HTMLElement>('[data-testid="tl-input-toggle-trigger"]'),
)

const handleClick = () => {
    if (props.disabled) return
    checked.value = !checked.value
}

const effectiveLabel = computed(() => {
    if (props.selectedLabel && checked.value) {
        return sanitize(marked(props.selectedLabel))
    }
    return sanitize(marked(props.label))
})

watch(checked, (val) => {
    emit('update:modelValue', val)
})

onMounted(() => {
    if (props.selected) {
        checked.value = true
    }
})
</script>

<style scoped lang="sass">
@import './Toggle.sass'
</style>
